import { flow, types, getRoot } from 'mobx-state-tree';

import api from '../../../common/api';
import {
    FooterBtnsConfig,
    MiniBanners,
    Popup,
    SiteStatuses,
    SocialIcons,
    CrashGamesConfig,
} from './';
import { llattempt } from '../../../common/utils';
import { applyLanguagesMeta } from '../../../common/utils/bootstrap-i18n';


const MarketDescription = types
    .model('MarketDescription', {
        id: types.identifierNumber,
        description: types.maybeNull(types.string),
    });

const SlotBattlesConfig = types
    .model('SlotBattlesConfig', {
        slotBattlesDisabled: false,
        timeToStart: types.maybeNull(types.number),
        timeToPlay: types.maybeNull(types.number),
        timeToWait: types.maybeNull(types.number),
    });

const EasyMembershipConfig = types
    .model('EasyMembershipConfig', {
        enabled: false,
        mode: 'independent',
        allowDepositUnverified: true,
        allowWithdrawUnverified: true,
        allowBonusRequestUnverified: true,
    });

const SiteConfig = types
    .model('SiteConfig', {
        // Group-scoped catalog from /api/global/config (Lang, Name, NativeName, SortOrder)
        languageList: types.optional(types.array(types.frozen()), []),
        name: types.string, //Site name (for Titles)
        defaultCustomerGroup: types.maybeNull(types.string), //Site name (for Titles)
        customerSubGroup: types.maybeNull(types.string), //Site name (for Titles)
        email: types.maybeNull(types.string), //site email
        liveChat: types.maybeNull(types.integer), //live chat id
        isVaixEnabled: false,
        bonusesCount: types.integer, //count of promos
        siteUrl: types.string, //For rel=canonical
        registration: types.boolean, //Is registration is on or off on website
        simpleSignup: types.boolean, //Is simpleSignup is on or off on website
        turnstile: types.maybeNull(types.string),
        currencies: types.maybeNull(types.array(types.string)), //array of currencies
        status: SiteStatuses, //For app internal flags
        miniBanners: MiniBanners, //for advertising on pre/live pages.
        popup: Popup, //for pupup message (store only last one)
        socialIcons: SocialIcons,
        slotBattlesConfig: types.optional(SlotBattlesConfig, {}),
        footerBtns: types.optional(FooterBtnsConfig, {}),
        crashGames: types.optional(CrashGamesConfig, {}),
        marketDescriptions: types.map(MarketDescription),
        isGuestCouponAllowed: false,
        isBetReservationAllowed: false,
        easyMembership: types.optional(EasyMembershipConfig, {}),
    })
    .actions((self) => ({
        _set(data) {
            if (!!data.isMaintenance) {
                self.status.setGlobalError(true, 'MAINTENANCE_ERROR');
                return;
            }
            self.languageList = Array.isArray(data.languageList) ? data.languageList : [];
            applyLanguagesMeta(self.languageList);
            self._ensureActiveLanguageAllowed();
            self.name = data.siteName;
            self.customerSubGroup = data.subgroup;
            self.siteUrl = data.siteUrl;
            self.registration = data.registration;
            self.simpleSignup = data?.simpleSignup || false;
            self.turnstile = data.turnstile || null;
            self.currencies = data.currencies;
            self.email = data.email;
            self.isVaixEnabled = !!data.isVaixEnabled;
            self.liveChat = data.liveChat || null;
            self.defaultCustomerGroup = data.group;
            self.isGuestCouponAllowed = !!data.isGuestCouponAllowed;
            self.isBetReservationAllowed = !!data.isBetReservationAllowed;
            self.easyMembership = {
                enabled: !!data.easyMembership?.enabled,
                mode: data.easyMembership?.mode === 'required' ? 'required' : 'independent',
                allowDepositUnverified: data.easyMembership?.allowDepositUnverified !== false,
                allowWithdrawUnverified: data.easyMembership?.allowWithdrawUnverified !== false,
                allowBonusRequestUnverified: data.easyMembership?.allowBonusRequestUnverified !== false,
            };

            if (data.slotBattle?.activeBattleRooms) {
                getRoot(self).slotsBattles.setActiveBattleRoomsCount(data.slotBattle.activeBattleRooms);
            } else {
                getRoot(self).slotsBattles.setActiveBattleRoomsCount(null);
            }
            self.slotBattlesConfig = {
                slotBattlesDisabled: !data.slotBattle?.canSlotBattle,
                timeToStart: data.slotBattle?.time.start,
                timeToPlay: data.slotBattle?.time.play,
                timeToWait: data.slotBattle?.time.winWait,
            };
            self.bonusesCount = parseInt(data.bonusesCount) || 0;
            if (self.socialIcons === 'GENERAL_ERROR') {
                self.status.setGlobalError(true);
            } else {
                self.socialIcons.setIcons(data.socialIcons);
            }
            getRoot(self).user.setCustomerGroup(data.group);
            if (data.footerFeatured && data.footerFeatured !== 'GENERAL_ERROR') {
                self.footerBtns.setConfig(data.footerFeatured);
            }

            if (data.crashGames && data.crashGames !== 'GENERAL_ERROR') {
                self.crashGames.setGames(data.crashGames);
            } else {
                self.crashGames.setMenuItemDisabled(true);
            }

            if (data.marketDescriptions) {
                Object.keys(data.marketDescriptions).forEach(key => {
                    self.marketDescriptions.put({
                        id: parseInt(key),
                        description: data.marketDescriptions[key]
                    })
                })
            }
        },

        getConfig: flow(function* fetch() {
            yield llattempt(
                () =>
                    api.config.get().then((response) => {
                        if (response.success) {
                            self._set(response.data);
                        } else {
                            self.status.setGlobalError(true, 'GENERAL_ERROR');
                        }
                    }),
                {
                    withNotifier: false,
                    at: 'SiteConfig.getConfig',
                    withParams: {},
                    onError: () => {
                        self.status.setGlobalError(true, 'GENERAL_ERROR');
                    },
                }
            );
        }),

        // After login/logout the group allow-list can shrink. If the active
        // language is no longer available, switch to the first allowed one
        // (setLanguage reloads so the dictionary matches).
        _ensureActiveLanguageAllowed() {
            const codes = self.languages;
            if (!codes.length) return;
            const user = getRoot(self).user;
            if (codes.includes(user.language)) return;
            user.setLanguage({ language: codes[0] });
        },

        setGlobalError(errorStatus, errorEvent = null, errorText) {
            self.status.setGlobalError(errorStatus, errorText);
            errorStatus && console.error('Website general error', errorEvent);
        },
    }))
    .views((self) => ({
        get languages() {
            return self.languageList.map((item) => item.Lang);
        },
        get siteName() {
            return self.name;
        }
    }));

export default SiteConfig;
